import { IContextualMenuItem, MessageBar, MessageBarType, SearchBox, Separator, Stack, Text, Toggle, useTheme } from '@fluentui/react';
import { RootState } from 'app/store';
import KeyValueCard from 'components/controls/KeyValueCard';
import LinkMenu from 'components/controls/LinkMenu';
import SimpleCard from 'components/controls/SimpleCard';
import { AppLinkTo, fadeIn, getSnapshotRef } from 'global/Application';
import { useApplication } from 'global/Application/Context';
import ComparisonEngine from 'global/Compare/ComparisonEngine';
import { addUpdateORE } from 'global/Reducer/dataSlice';
import { updateWorkspace } from 'global/Reducer/statusSlice';
import { ArrayToLookup } from 'global/Utility/TSFunctions';
import {
    $cat_$capital_prime_id,
    $cat_$florida_prime_id
} from 'model/ConfigCategoryConstants';
import {
    IBenefit as FAM_IBenefit,
    IPharmacyORE as FAM_IPharmacyORE,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import CustomPanel, { EPanelStyleType } from 'views/BlueRelationship/Compare/CustomPanel';
import CompareInformation from 'views/BlueRelationship/Controls/CompareInformation';
import { IPharmacyContextProps } from './PharmacyListContext';

interface IPharmacyExtractViewModel {
    plan: FAM_IPlan;
    bcbsRxBenefit: FAM_IBenefit | null;
    primeBenefit: FAM_IBenefit | null;
    capitalPrimeBenefit: FAM_IBenefit | null;
    floridaPrimeBenefit: FAM_IBenefit | null;
    pharmacyExtract: FAM_IPharmacyORE | null;
    selected?: boolean;
    compare?: ComparisonEngine;
    selectable?: boolean;
    searchTags: string[];
}

export interface IPharmacyListProps {
    contextProps: IPharmacyContextProps;
}

const PharmacyList: React.FC<IPharmacyListProps> = (props) => {

    const state = useSelector((state: RootState) => state);
    const dispatch = useDispatch();
    const history = useHistory();

    const theme = useTheme();

    const [quickFilter, setQuickFilter] = React.useState<RegExp | null>(null);
    const [isSelecting, setIsSelecting] = React.useState(false);

    const application = useApplication();

    const divStyle = useMemo(() => {
        const style: React.CSSProperties = {
            backgroundColor: theme.semanticColors.menuBackground,
            border: `1px solid ${theme.semanticColors.bodyDivider}`,
            borderRadius: '8px',
            padding: '5px'
        };

        const styleFaded: React.CSSProperties = {
            ...style,
            opacity: 0.2,
        };

        return {
            style: style,
            styleFaded: styleFaded,
        }
    }, [theme]);

    const [comparisonState, setComparisonState] = application.comparisonState;

    const generateViewModel = () => {

        const config = state.data.config;

        const vmList: IPharmacyExtractViewModel[] = [];

        const benefitLookup = ArrayToLookup(state.data.json.benefits);

        const pharmacyExtractLookup = ArrayToLookup(state.data.json.pharmacyORE ?? []);

        const floridaPrimeCategory = state.data.config.cache?.map_bcbs_config_category[$cat_$florida_prime_id];

        const capitalPrimeCategory = state.data.config.cache?.map_bcbs_config_category[$cat_$capital_prime_id];

        state.data.json.plans.forEach(plan => {

            const vm: IPharmacyExtractViewModel = {
                plan: plan,
                bcbsRxBenefit: null,
                primeBenefit: null,
                capitalPrimeBenefit: null,
                floridaPrimeBenefit: null,
                selectable: true,
                pharmacyExtract: null,
                searchTags: [],
            }

            vmList.push(vm);

            plan.benefitRefs.forEach(eachBenefitRef => {
                const benefit = benefitLookup[eachBenefitRef.id];

                if (!benefit)
                    throw new Error(`Benefit not found for id: ${eachBenefitRef.id}`);

                const isBCBSRx = benefit?.bcbs_config_categoryid === config.cache.map_bcbs_config_category_named['Pharmacy']?.bcbs_config_categoryid;

                const isFloridaPrime = benefit?.bcbs_config_categoryid === floridaPrimeCategory?.bcbs_config_categoryid;
                const isCapitalPrime = benefit?.bcbs_config_categoryid === capitalPrimeCategory?.bcbs_config_categoryid;

                if (isBCBSRx)
                    vm.bcbsRxBenefit = benefit;

                if (isFloridaPrime || isCapitalPrime)
                    vm.primeBenefit = benefit;

                if (isFloridaPrime)
                    vm.floridaPrimeBenefit = benefit;

                if (isCapitalPrime)
                    vm.capitalPrimeBenefit = benefit;

                if (isBCBSRx || isFloridaPrime || isCapitalPrime) {
                    vm.searchTags.push(benefit.name);
                }

            });

            // set pharmacy extract
            vm.searchTags.push(plan.name);

            if (vm.primeBenefit) {
                vm.pharmacyExtract = pharmacyExtractLookup[plan.id!] ?? null;
            }

            if (quickFilter) {
                vm.selectable = false;
                if (vm.searchTags.some(t => quickFilter.test(t)))
                    vm.selectable = true;
            }

        });

        return vmList;
    }

    const [plansViewModel, setPlansViewModel] = useState(generateViewModel());

    useEffect(() => {
        setPlansViewModel(generateViewModel());
    }, [state.data.json.pharmacyORE, isSelecting, quickFilter]);

    const planCardsMenu = useMemo(() => {
        const menuItems: IContextualMenuItem[] = [
            {
                key: 'card',
                text: 'Card View - Sorted By Entry Sequence',
            },
            {
                key: 'cardSorted',
                text: 'Card View - Sorted By Name',
            },
            {
                key: 'grid',
                text: 'Grid View',
            },
        ];
        return menuItems;
    }, []);

    const getActionMenuItems = () => {
        const items: IContextualMenuItem[] = [];

        const plansViewModelSelected = plansViewModel.filter(t => t.selected);

        if (plansViewModelSelected.length > 0)
            items.push({
                key: 'setready',
                name: `Set Ready for Release`,
                icon: 'DocumentManagement',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const ore = plansViewModel
                        .filter(t => t.selected)
                        .map(eachVM => {
                            const ore: FAM_IPharmacyORE = {
                                ...eachVM.pharmacyExtract!,
                                releaseStatus: "Ready for Release"
                            };
                            return ore;
                        });

                    dispatch(addUpdateORE(ore));

                },
            });

        if (plansViewModelSelected.length > 0)
            items.push({
                key: 'updateplandesign',
                name: `Update Plan Design`,
                icon: 'DocumentManagement',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const newPharmacyPlanDesign = prompt("Enter new Plan Design", "")

                    if (!newPharmacyPlanDesign)
                        return;

                    const ore = plansViewModel
                        .filter(t => t.selected)
                        .map(eachVM => {
                            const ore: FAM_IPharmacyORE = {
                                ...eachVM.pharmacyExtract!,
                                newPharmacyPlanDesign: newPharmacyPlanDesign ?? ""
                            };
                            return ore;
                        });

                    dispatch(addUpdateORE(ore));

                },
            });

        if (plansViewModelSelected.length > 0)
            items.push({
                key: 'updateclientnumber',
                name: `Update Client Number`,
                icon: 'DocumentManagement',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    const newClientNumber = prompt("Enter client number", "")

                    if (!newClientNumber)
                        return;

                    const ore = plansViewModel
                        .filter(t => t.selected)
                        .map(eachVM => {
                            const ore: FAM_IPharmacyORE = {
                                ...eachVM.pharmacyExtract!,
                                newClientNumber: newClientNumber ?? ""
                            };
                            return ore;
                        });

                    dispatch(addUpdateORE(ore));

                },
            });

        if (isSelecting)
            items.push({
                key: 'selectall',
                icon: 'DocumentManagement',
                name: 'Select All',
                onClick: (event, item) => {
                    event?.stopPropagation();
                    event?.preventDefault();

                    setPlansViewModel(t => t.map(each => {
                        if (each.selectable && each.primeBenefit)
                            return ({
                                ...each,
                                selected: true
                            })

                        return each;
                    }));
                },
            })

        items.push({
            key: 'select',
            name: 'Selection Mode',
            icon: isSelecting ? 'CheckboxComposite' : 'Checkbox',
            onClick: (event, item) => {
                event?.stopPropagation();
                event?.preventDefault();

                setIsSelecting(!isSelecting);
            },
        });

        return items;
    }

    const onRenderPlansByCard = () => {

        return <Stack
            horizontal
            wrap
            verticalFill
            tokens={{ childrenGap: 15 }}
            horizontalAlign="start"
            style={{
                padding: '15px',
                marginBottom: '15px'
            }}
        >
            {plansViewModel.map(eachVM => {

                let error: JSX.Element | null = null;

                if (!eachVM.pharmacyExtract)
                    error = <Text variant='xSmall'>
                        Missing Pharmacy Extract
                    </Text>

                if (!eachVM.primeBenefit)
                    error = <Text variant='xSmall' styles={{ root: { width: '120px' } }}>
                        Prime Benefit Not Found!
                    </Text>

                if (eachVM.capitalPrimeBenefit && !props.contextProps.isCapitalPrime)
                    error = <Text variant='xSmall' styles={{ root: { width: '120px' } }}>
                        Capital Prime Benefit
                    </Text>

                if (eachVM.floridaPrimeBenefit && !props.contextProps.isFloridaPrime)
                    error = <Text variant='xSmall' styles={{ root: { width: '120px' } }}>
                        Florida Prime Benefit
                    </Text>

                if (eachVM.plan.billingOnlyPlan)
                    error = <Text variant='xSmall'>
                        Admin Only Plan
                    </Text>

                if (!eachVM.selectable)
                    error = <Text variant='xSmall'>
                        Not Selectable
                    </Text>


                const onClickPlan = () => {
                    if (eachVM.floridaPrimeBenefit)
                        history.push(AppLinkTo(state, {
                            type: 'floridaPrimeRxPlan',
                            id: eachVM.plan.id!
                        }));

                    if (eachVM.capitalPrimeBenefit)
                        history.push(AppLinkTo(state, {
                            type: 'capitalPrimeRxPlan',
                            id: eachVM.plan.id!
                        }));
                }

                const onClickSelect = () => {
                    setPlansViewModel(t => t.map(each => {
                        if (each.plan.id === eachVM.plan.id)
                            return { ...each, selected: !each.selected };
                        return each;
                    }));
                }

                const onClick = isSelecting
                    ? eachVM.selectable ? onClickSelect : undefined
                    : eachVM.selectable ? onClickPlan : undefined;

                const keyvalue = eachVM.pharmacyExtract
                    ? [
                        { key: "Release Status", value: eachVM.pharmacyExtract.releaseStatus },
                        { key: "Pharmacy Plan Design", value: eachVM.pharmacyExtract.pharmacyPlanDesign },
                        { key: "New Pharmacy Plan Design", value: eachVM.pharmacyExtract.newPharmacyPlanDesign },
                        { key: "New Client Number", value: eachVM.pharmacyExtract.newClientNumber },
                    ]
                    : [];

                const disabled = !eachVM.pharmacyExtract || !!error;

                return (
                    <Stack key={eachVM.plan.id}
                        tokens={{ childrenGap: 5 }}
                        style={eachVM.selectable ? divStyle.style : divStyle.styleFaded}
                    >

                        <SimpleCard
                            header={"Plan"}
                            description={eachVM.plan.name}
                            selected={eachVM.selected}
                            disabled={disabled}
                            onCallout={() => {
                                return <KeyValueCard keyValuePairs={keyvalue} />
                            }}
                            onClick={onClick}
                        />
                        {eachVM.pharmacyExtract &&
                            <Toggle
                                inlineLabel
                                label="Release"
                                styles={{ root: { marginBottom: '0' } }}
                                onClick={e => e.stopPropagation()}
                                disabled={disabled || isSelecting}
                                onKeyDown={e => e.stopPropagation()}
                                checked={eachVM.pharmacyExtract.releaseStatus === "Ready for Release"}
                                onChange={(e, checked) => {
                                    setIsSelecting(false);
                                    const ore: FAM_IPharmacyORE = {
                                        ...eachVM.pharmacyExtract!,
                                        releaseStatus: checked ? "Ready for Release" : "In Progress"
                                    };
                                    dispatch(addUpdateORE([ore]));
                                }}
                            />
                        }
                        {error}

                    </Stack>
                );
            })}
        </Stack>;
    }

    const onRenderCompareInformation = () => {

        if (!comparisonState) {
            return null;
        }

        return (
            <CompareInformation
                onCancel={() => { setComparisonState(null); }}
                origin={comparisonState?.origin?.snapshot ? getSnapshotRef(comparisonState?.origin?.snapshot) : undefined}
                target={comparisonState?.target?.snapshot ? getSnapshotRef(comparisonState?.target?.snapshot) : undefined}
                showDifferencesOnly={comparisonState?.configuration?.showDifferencesOnly ?? false}
                onShowDifferencesOnlyChange={(showDifferencesOnly) => {
                    setComparisonState(t => (
                        {
                            ...t!,
                            configuration: {
                                ...t?.configuration!,
                                showDifferencesOnly: showDifferencesOnly ?? false
                            }
                        })
                    )
                }}
            />
        )
    }

    const onRenderPlans = () => {
        return (
            <CustomPanel title={"Plans"}
                canCollapse={true}
                defaultCollapsed={false}
                contentStyle={{ overflow: 'visible' }}
                headerRightMenuItems={getActionMenuItems()}
                styleType={EPanelStyleType.Gray}
            >
                <Stack horizontal horizontalAlign='space-between' verticalAlign='start'>
                    <LinkMenu
                        styles={{ root: { marginTop: '12px' } }}
                        menuItems={planCardsMenu}
                        selectedKey={'card'}
                        disabled
                        onChange={(event, menuItem) => {
                            dispatch(updateWorkspace({ pharmacy: menuItem.key }));
                        }}
                    />
                    <SearchBox
                        styles={{
                            root: {
                                transform: 'scale(0.7,0.7)',
                                transformOrigin: 'right',
                                backgroundColor: 'transparent',
                                width: '210px',
                                marginTop: '5px',
                            }
                        }}
                        placeholder='Search Plans, Benefits'
                        onClear={() => setQuickFilter(null)}
                        onSearch={(newValue) => {
                            try {
                                var searchRegex = newValue ? new RegExp(newValue, 'i') : null;
                                setQuickFilter(searchRegex);
                            } catch {
                                setQuickFilter(null);
                            }
                        }}
                    />
                </Stack>

                <Separator />
                {onRenderPlansByCard()}
            </CustomPanel>
        );
    }

    if (state.data.json.groupCollectStatus)
        return (
            <MessageBar className={fadeIn} messageBarType={MessageBarType.warning}>
                This snapshot was last processed with Group Collect integration, Pharmacy/ORE requires you to make a save in Group Library before you can continue.
            </MessageBar>
        );

    return (
        <Stack tokens={{ childrenGap: '15px' }}>
            {onRenderCompareInformation()}
            {onRenderPlans()}
        </Stack>
    )

};

export default PharmacyList;